// AppContext.js
import React, { createContext, useContext, useReducer, useEffect, useMemo } from 'react';
import { financialService } from '../utils/api';
import { APP_CONFIG } from '../appConfig';

const initialState = {
  isAuthenticated: false,
  otpSent: false,
  access_token: null,
  isLoading: false,
  clientName: "",
  email: "",
  phoneNumber: "",
  city: '',
  age: 0,
  retirementAge: 0,
  birthYear: 0,
  yearsForRetirement: -1,
  monthlyExpensesInRetirement: 0,
  emergencyFunds: 0,
  riskProfile: '',
  netWorth: 0,
  fundingScore: 0,
  corpusRequired: 0,
  amountRequiredToday: 0,
  submissionSuccess: false,
  receiveReport: true,
  requestCallback: false,
  kidsInfo: [],
  kidsEducation: [],
  kidsPlanningValid: false,

  salary: 0,
  spouseSalary: 0,
  otherIncome: 0,
  
  goals: [],
  
  monthlyExpense: 0,
  oneTimeYearly: 0,
  annualInvestments: 0,
  sip: 0,
  
  bankBalance: 0,
  fixedDeposit: 0,
  retirementFund: 0,
  bonds: 0,
  
  stocks: 0,
  unlisted: 0,
  pms: 0,
  
  globalEquity: 0,
  globalMF: 0,
  globalBonds: 0,
  
  gold: 0,
  otherInvestments: 0,
  realEstate: 0,
  
  debtMF: 0,
  equityMF: 0,
  hybridMF: 0,
  invits: 0,
  
  total_debt: 0,
  total_income: 0,
  total_equity: 0,
  total_alternative_funds: 0,
  total_global_equity: 0,
  high_liquid: 0,
  low_liquid: 0,
  post_retirement: 0,

  occupation: '',
  employeer: '',
  dependents: 0,

  objective: '',
  concerns: '',
  yearsInEquity: '',

  residenceType: '',
  insuranceStatus: '',

  // NEW: stepper separation
  stepperMode: 'goalPlanning',
  activeStep_goalPlanning: 0,
  activeStep_assetAllocation: 0
};

const ActionTypes = {
  SET_AUTH_STATE: 'SET_AUTH_STATE',
  SET_FORM_DATA: 'SET_FORM_DATA',
  UPDATE_FIELD: 'UPDATE_FIELD',
  UPDATE_KIDS_INFO: 'UPDATE_KIDS_INFO',
  SET_ACTIVE_STEP_GOAL: 'SET_ACTIVE_STEP_GOAL',
  SET_ACTIVE_STEP_ASSET: 'SET_ACTIVE_STEP_ASSET',
  SET_STEPPER_MODE: 'SET_STEPPER_MODE',
  RESET_FORM: 'RESET_FORM',
  LOAD_FROM_STORAGE: 'LOAD_FROM_STORAGE',
  ADD_GOAL: 'ADD_GOAL',
  REMOVE_GOAL: 'REMOVE_GOAL',
  LOAD_USER_DATA: 'LOAD_USER_DATA',
  SET_LOADING: 'SET_LOADING'
};

const mapUserDataToState = (apiData) => {
  const mappedData = {};
  
  // Map user data
  if (apiData.userData && apiData.userData.length > 0) {
    const user = apiData.userData[0];
    mappedData.email = user.email || "";
    // mappedData.clientName = user.full_name || "";
    mappedData.city = user.city || "";
    mappedData.birthYear = user.birth_year || 0;
    mappedData.age = user.birth_year ? new Date().getFullYear() - user.birth_year : 0;
    mappedData.riskProfile = user.risk_profile || "";
    mappedData.netWorth = user.net_worth || 0;
    mappedData.emergencyFunds = user.emergency_funds || 0;
    mappedData.fundingScore = user.funding_score || 0;
    mappedData.retirementAge = user.planned_retirement_age || 0;
    mappedData.monthlyExpensesInRetirement = user.monthly_expenses_in_retirement || 0;
    mappedData.requestCallback = user.request_callback || false;
    mappedData.receiveReport = user.request_report || true;
    mappedData.yearsForRetirement = user.planned_retirement_age ? 
      user.planned_retirement_age - (new Date().getFullYear() - (user.birth_year || 0)) : -1;
    mappedData.occupation = user.occupation || '';
  mappedData.employer = user.employer || '';
  mappedData.dependents = user.dependents || 0;
  mappedData.objective = user.objective || '';
  mappedData.concerns = user.concerns || '';
  mappedData.yearsInEquity = user.years_in_equity || '';
  }

  // Map goals
  if (apiData.goals) {
    mappedData.goals = apiData.goals.map(goal => ({
      id: goal.id,
      type: goal.goal_type,
      currentValue: goal.current_value,
      targetYear: goal.target_date,
      FV_goal: goal.future_value,
      // createdAt: new Date(goal.created_at)
    }));
  }

  // Map children data
  if (apiData.children) {
    mappedData.kidsInfo = apiData.children.map(child => ({
      name: child.name,
      yearOfBirth: child.year_of_birth,
      age: child.age
    }));
  
    mappedData.kidsEducation = apiData.children.map(child => ({
      name: child.name,
      yearOfBirth: child.year_of_birth,
      Age: child.age,
      ug: child.ug_college,
      ug_fees: child.ug_college_fees,
      ug_fees_FV: child.ug_college_fees_fv,
      pg: child.pg_college,
      pg_fees: child.pg_college_fees,
      pg_fees_FV: child.pg_college_fees_fv
    }));
  }

  // Map investments
  if (apiData.investments && apiData.investments.length > 0) {
    const inv = apiData.investments[0];
    mappedData.bankBalance = inv.bank_balance || 0;
    mappedData.fixedDeposit = inv.fixed_deposit || 0;
    mappedData.retirementFund = inv.retirement_fund || 0;
    mappedData.bonds = inv.bonds || 0;
    mappedData.debtMF = inv.debt_mf || 0;
    mappedData.equityMF = inv.equity_mf || 0;
    mappedData.hybridMF = inv.hybrid_mf || 0;
    mappedData.invits = inv.invits || 0;
    mappedData.stocks = inv.stocks || 0;
    mappedData.pms = inv.pms || 0;
    mappedData.unlisted = inv.unlisted || 0;
    mappedData.globalEquity = inv.global_equity || 0;
    mappedData.globalBonds = inv.global_bonds || 0;
    mappedData.globalMF = inv.global_mf || 0;
    mappedData.realEstate = inv.real_estate || 0;
    mappedData.gold = inv.gold || 0;
    mappedData.otherInvestments = inv.other_investments || 0;
  }

  // Map cashflow
  if (apiData.cashflow && apiData.cashflow.length > 0) {
    const cf = apiData.cashflow[0];
    mappedData.salary = cf.salary || 0;
    mappedData.spouseSalary = cf.spouse_salary || 0;
    mappedData.otherIncome = cf.other_income || 0;
    mappedData.monthlyExpense = cf.monthly_expense || 0;
    mappedData.oneTimeYearly = cf.one_time_yearly || 0;
    mappedData.annualInvestments = cf.annual_investments || 0;
    mappedData.sip = cf.sip || 0;
  }

  // Calculate totals
  mappedData.total_income = (mappedData.salary || 0) + (mappedData.spouseSalary || 0) + (mappedData.otherIncome || 0);
  mappedData.total_debt = (mappedData.bankBalance || 0) + (mappedData.fixedDeposit || 0) + (mappedData.retirementFund || 0) + (mappedData.bonds || 0) + (mappedData.debtMF || 0);
  mappedData.total_equity = (mappedData.equityMF || 0) + (mappedData.stocks || 0) + (mappedData.pms || 0) + (mappedData.unlisted || 0);
  mappedData.total_global_equity = (mappedData.globalEquity || 0) + (mappedData.globalMF || 0) + (mappedData.globalBonds || 0);
  mappedData.total_alternative_funds = (mappedData.gold || 0) + (mappedData.realEstate || 0) + (mappedData.otherInvestments || 0);
  mappedData.high_liquid = (mappedData.bankBalance || 0) + (mappedData.fixedDeposit || 0);
  mappedData.low_liquid = mappedData.total_equity + mappedData.total_alternative_funds;

  return mappedData;
};

const appReducer = (state, action) => {
  switch (action.type) {
    case ActionTypes.SET_AUTH_STATE:
      return {
        ...state,
        ...action.payload
      };

    case ActionTypes.SET_FORM_DATA:
      const newState = {
        ...state,
        ...action.payload
      };
      return newState; // Add this line
    case ActionTypes.LOAD_USER_DATA:
      const mappedData = mapUserDataToState(action.payload);
        return {
          ...state,
          ...mappedData,
          isLoading: false
        };
        
    case ActionTypes.SET_LOADING:
      return {
        ...state,
        isLoading: action.payload
      };

    case ActionTypes.UPDATE_FIELD:
      return { ...state, [action.field]: action.value };
    case ActionTypes.UPDATE_KIDS_INFO:
      return { ...state, kidsInfo: action.value };
    case ActionTypes.SET_ACTIVE_STEP_GOAL:
      return { ...state, activeStep_goalPlanning: action.value };
    case ActionTypes.SET_ACTIVE_STEP_ASSET:
      return { ...state, activeStep_assetAllocation: action.value };
    case ActionTypes.SET_STEPPER_MODE:
      return { ...state, stepperMode: action.value };
    case ActionTypes.RESET_FORM:
      return { ...initialState, isAuthenticated: state.isAuthenticated };
    case ActionTypes.ADD_GOAL:
      return { ...state, goals: [...state.goals, action.payload] };
    case ActionTypes.REMOVE_GOAL:
      return { ...state, goals: state.goals.filter((_, index) => index !== action.payload) };
    case ActionTypes.LOAD_FROM_STORAGE:
      return { ...state, ...action.payload };
    default:
      return state;
  }
};

const AppContext = createContext();
const STORAGE_KEY = APP_CONFIG.persistedStorageKey;

export const AppProvider = ({ children }) => {
  const [state, dispatch] = useReducer(appReducer, initialState);


  useEffect(() => {
    const dataToSave = { ...state };
    delete dataToSave.activeStep_goalPlanning;
    delete dataToSave.activeStep_assetAllocation;
    delete dataToSave.stepperMode;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(dataToSave));
  }, [state]);

  const actions = {
    setAuthState: (authData) => { dispatch({ type: ActionTypes.SET_AUTH_STATE, payload: authData});},

    sendOTP: (userData) => { dispatch({ type: ActionTypes.SET_AUTH_STATE,
        payload: {
          otpSent: true,
          clientName: userData.name,
          email: userData.email,
          phoneNumber: userData.phoneNo
        }
      });
    },

    verifyOTP: async (accessToken, phoneNumber) => {
      dispatch({
        type: ActionTypes.SET_AUTH_STATE,
        payload: {
          isAuthenticated: true,
          access_token: accessToken,
          phoneNumber: phoneNumber
        }
      });
      
      if (accessToken && phoneNumber) {
        dispatch({ type: ActionTypes.SET_LOADING, payload: true });
        
        try {
          const userData = await financialService.loadUserData(phoneNumber);
          console.log('Raw userData:', userData);
          
          if (userData) {
            dispatch({
              type: ActionTypes.LOAD_USER_DATA,
              payload: userData // Remove .data
            });
          } else {
            dispatch({ type: ActionTypes.SET_LOADING, payload: false });
          }
        } catch (error) {
          console.error('Error loading user data:', error);
          dispatch({ type: ActionTypes.SET_LOADING, payload: false });
        }
      }
    },

    logout: () => {
      localStorage.removeItem(STORAGE_KEY);
      dispatch({ type: ActionTypes.RESET_FORM });
    },
    updateFormData: (field, value) => dispatch({ type: ActionTypes.UPDATE_FIELD, field, value }),
    addGoal: (goal) => dispatch({ type: ActionTypes.ADD_GOAL, payload: goal }),
    removeGoal: (index) => dispatch({ type: ActionTypes.REMOVE_GOAL, payload: index }),
    updateKidsInfo: (kidsInfo) => dispatch({ type: ActionTypes.UPDATE_KIDS_INFO, value: kidsInfo }),
    resetForm: () => {
      localStorage.removeItem(STORAGE_KEY);
      dispatch({ type: ActionTypes.RESET_FORM });
    },
    // Step control
    setStepperMode: (mode) => dispatch({ type: ActionTypes.SET_STEPPER_MODE, value: mode }),
    setActiveStepGoal: (val) => dispatch({ type: ActionTypes.SET_ACTIVE_STEP_GOAL, value: val }),
    setActiveStepAsset: (val) => dispatch({ type: ActionTypes.SET_ACTIVE_STEP_ASSET, value: val }),
    nextStepGoal: () => dispatch({ type: ActionTypes.SET_ACTIVE_STEP_GOAL, value: state.activeStep_goalPlanning + 1 }),
    prevStepGoal: () => dispatch({ type: ActionTypes.SET_ACTIVE_STEP_GOAL, value: Math.max(state.activeStep_goalPlanning - 1, 0) }),
    nextStepAsset: () => dispatch({ type: ActionTypes.SET_ACTIVE_STEP_ASSET, value: state.activeStep_assetAllocation + 1 }),
    prevStepAsset: () => dispatch({ type: ActionTypes.SET_ACTIVE_STEP_ASSET, value: Math.max(state.activeStep_assetAllocation - 1, 0) })
  };

  const value = { state: { ...state }, actions };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};

export const useAppContext = () => {
  const context = useContext(AppContext);
  if (!context) throw new Error('useAppContext must be used within an AppProvider');
  return context;
};

export { ActionTypes, AppContext };
